import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { ArrowUpRight, Check, Copy } from "lucide-react";
import { create } from "zustand";
import type { GroomingRunnerEvent, GroomingToolsetBridge } from "@ttc-spectree/toolbox-contract";
import { QueueBadge, queueBadgeStatus, safeQueueKey, useOnQueueSettled } from "@/components/grooming/queue-status-badge";
import { useGroomingBatches } from "@/hooks/queries/use-grooming";
import { useGroomingQueue } from "@/hooks/use-grooming-queue";
import { AuthorQuestionsEditor } from "@/components/grooming/author-questions-editor";
import { Chip } from "@/components/grooming/grooming-chips";
import { useMe } from "@/hooks/queries/use-me";
import {
  useAuthorQuestionsDraft,
  useAuthorQuestionsLatestAsk,
  usePostAuthorQuestions,
  workItemKeys,
} from "@/hooks/queries/use-work-items";
import type { LatestAuthorQuestionAsk, WorkItemDetail } from "@/lib/api/work-items";
import { useCan } from "@/hooks/queries/use-toolbox-access";
import { askCountWords, relativeDays, shortDate } from "@/lib/grooming/ask-words";
import { groomingErrorMessage } from "@/lib/grooming/error-messages";
import { useRunnerBridge, useRunnerEvents } from "@/lib/grooming/runner-bridge";
import { rememberQuestionsPosted, rememberQueueTitle } from "@/lib/grooming/queue-titles";
import { showGroomingToast } from "@/lib/grooming/toasts";
import { useToolboxBridge } from "@/lib/toolbox/bridge";
import { cn } from "@/lib/utils";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";

/**
 * "Questions for the author" for an item below the grooming standard (epic
 * PI-SPEC-SW-E280, feature PI-SPEC-SW-1401; E280 decision 4).
 *
 * The stored draft (`GET .../author-questions/draft`) is shown in the app and
 * in a browser. Inside a contract-2 SpecTree Toolbox, an item with no current
 * draft gets one automatically: after the pane has been open for
 * {@link AUTO_DRAFT_DWELL_MS}, `bridge.grooming.draftAuthorQuestions` runs
 * Claude Code on the person's own seat, which returns the questions and
 * stores them. Because that spends the person's Claude seat it runs only
 * without a current draft, never while a batch runs, once per item per page
 * load, and one at a time across the backlog. A browser or a contract-1 app
 * with no draft says where questions are drafted.
 *
 * In a contract-3 app (epic PI-SPEC-SW-E284) the draft goes through the
 * app's run queue instead (`enqueueAuthorQuestions`): the card shows the
 * entry's "Queued · #n" / "Running" badge, re-reads the stored draft when it
 * settles, and shows a failed entry's reason with Try again. A batch running
 * no longer holds the draft back (the queue orders the runs), but it still
 * runs only without a current draft, once per item per page load, and never
 * while another author-questions draft is queued or running.
 *
 * Since epic PI-SPEC-SW-E292 (feature PI-SPEC-SW-1498, UX-PM-06, frames D3
 * and D4), a person with `post-single-brief` gets the questions as an
 * editable list (AuthorQuestionsEditor) and posts exactly what is in the
 * boxes as one comment that @mentions the item's creator; no confirm dialog.
 * Every post is stored as an ask, so after posting and on every later visit
 * the card shows the stored ask (Asked by, when, who was notified, the
 * questions as posted, See the comment in ADO) with Ask again instead of
 * Post. Ask again opens the editor with the questions as last posted, so the
 * PM's edits to the first ask are kept (PKG-PM-D Mac walk, 2026-10-23), and
 * posts a new comment. Everyone else sees the questions read-only with Copy and, once
 * asked, the asked view without Ask again. Drafting is unchanged.
 *
 * Every question is untrusted model output: rendered as React text or a
 * textarea value only.
 */

/** How long the pane stays on an item before its questions are drafted. */
export const AUTO_DRAFT_DWELL_MS = 1500;

/** The grooming contract that returns drafted questions from `draftAuthorQuestions`. */
export const AUTO_DRAFT_MIN_CONTRACT = 2;

interface AuthorDraftState {
  /** `project/itemId` of the draft running now; one at a time. */
  inFlight: string | null;
  /** Items already auto-drafted (or tried) on this page load: never run twice. */
  attempted: Record<string, true>;
  /** Questions a run returned, until the stored draft is read back. */
  results: Record<string, string[]>;
  /** Plain words for a failed run, by item. */
  failures: Record<string, string>;
}

/** Shared across panes, so moving J/K between items never starts a second run. */
export const useAuthorDraftStore = create<AuthorDraftState>(() => ({
  inFlight: null,
  attempted: {},
  results: {},
  failures: {},
}));

/** Test helper: forget every run. */
export function resetAuthorDraftStore(): void {
  useAuthorDraftStore.setState({ inFlight: null, attempted: {}, results: {}, failures: {} });
}

function itemKey(project: string, itemId: string): string {
  return `${project.toLowerCase()}/${itemId}`;
}

/**
 * Run one draft. Lives outside the component so a run the person moved away
 * from still finishes, stores its result and refreshes the stored draft.
 */
function startDraft(runner: GroomingToolsetBridge, queryClient: QueryClient, project: string, itemId: string): void {
  const key = itemKey(project, itemId);
  const store = useAuthorDraftStore;
  if (store.getState().inFlight !== null) return;
  store.setState((state) => {
    const { [key]: _old, ...failures } = state.failures;
    return { inFlight: key, attempted: { ...state.attempted, [key]: true }, failures };
  });

  const finish = (update: Partial<Pick<AuthorDraftState, "results" | "failures">>) => {
    store.setState((state) => ({
      inFlight: state.inFlight === key ? null : state.inFlight,
      results: update.results ?? state.results,
      failures: update.failures ?? state.failures,
    }));
    void queryClient.invalidateQueries({ queryKey: workItemKeys.authorQuestionsDraft("ado", project, itemId) });
  };

  void runner.draftAuthorQuestions(project, itemId).then(
    (result) => {
      const state = store.getState();
      if (!result.ok) {
        finish({ failures: { ...state.failures, [key]: result.message } });
      } else if (result.questions && result.questions.length > 0) {
        finish({ results: { ...state.results, [key]: result.questions } });
      } else {
        finish({});
      }
    },
    () => {
      finish({ failures: { ...store.getState().failures, [key]: "SpecTree Toolbox could not draft the questions." } });
    }
  );
}

/**
 * True while a grooming batch runs in this project: one the batch list shows
 * running, or one this app reported starting since the pane opened.
 */
function useBatchRunning(project: string, runner: GroomingToolsetBridge | null): boolean {
  const batches = useGroomingBatches(project, { enabled: runner !== null });
  const [live, setLive] = useState(false);
  const onEvent = useCallback((event: GroomingRunnerEvent) => {
    if (event.type === "item-started" || event.type === "item-progress") setLive(true);
    if (event.type === "batch-finished" || event.type === "batch-stopped") setLive(false);
  }, []);
  useRunnerEvents(runner, onEvent);
  const listed = (batches.data ?? []).some((batch) => batch.status === "running");
  return live || listed;
}

function Shimmer() {
  return (
    <div className="flex flex-col gap-2" data-testid="author-questions-drafting" aria-hidden="true">
      {["w-[92%]", "w-[76%]", "w-[58%]"].map((width) => (
        <div key={width} className={cn("h-3 animate-pulse rounded-md bg-[#232328]", width)} />
      ))}
    </div>
  );
}

/** "Asked by Tony Truong on Oct 14 · 6 days ago · Maria Lopez was notified in ADO · asked twice", in parts. Pure. */
export function askedLineParts(
  ask: Pick<LatestAuthorQuestionAsk, "postedByName" | "createdAt" | "mentioned" | "mentionedName" | "askCount">,
  now: Date = new Date()
): { lead: string; rest: string[] } {
  const date = shortDate(ask.createdAt);
  const lead = `Asked by ${ask.postedByName ?? "someone"}${date ? ` on ${date}` : ""}`;
  const rest: string[] = [];
  const ago = relativeDays(ask.createdAt, now);
  if (ago) rest.push(ago);
  rest.push(
    ask.mentioned
      ? `${ask.mentionedName ?? "the author"} was notified in ADO`
      : "the author was not named in ADO, so they were not emailed"
  );
  const count = askCountWords(ask.askCount);
  if (count) rest.push(count);
  return { lead, rest };
}

/** After asking (frame D4): who asked and when, who was notified, the questions as posted, Ask again. */
function AskedView({
  ask,
  itemUrl,
  onAskAgain,
}: {
  ask: LatestAuthorQuestionAsk;
  itemUrl: string;
  /** Absent without `post-single-brief`: no Ask again. */
  onAskAgain?: (() => void) | undefined;
}) {
  const { lead, rest } = askedLineParts(ask);
  return (
    <div className="flex flex-col gap-2.5" data-testid="author-questions-asked">
      <p className="text-[13px] leading-normal text-muted-foreground" data-testid="author-questions-asked-line">
        <b className="font-semibold text-foreground">{lead}</b>
        {rest.map((part) => ` · ${part}`).join("")}
      </p>
      <ol
        className="flex list-decimal flex-col gap-1 border-l-2 border-[#27272A] pl-[30px] text-[13px] leading-normal text-muted-foreground"
        data-testid="author-questions-asked-list"
      >
        {ask.questions.map((question, index) => (
          <li key={index}>{question}</li>
        ))}
      </ol>
      <div className="flex items-center gap-2 border-t border-[#1F1F23] pt-3">
        <a
          href={itemUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-[#6CC4FF] hover:underline"
          data-testid="author-questions-see-comment"
        >
          <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
          See the comment in ADO
        </a>
        <span className="flex-1" />
        {onAskAgain && (
          <button
            type="button"
            className="inline-flex h-[30px] items-center rounded-lg border border-[#27272A] bg-[#1A1A1E] px-2.5 text-[13px] font-medium hover:bg-[#222227]"
            data-testid="author-questions-ask-again"
            onClick={onAskAgain}
          >
            Ask again
          </button>
        )}
      </div>
    </div>
  );
}

export interface AuthorQuestionsCardProps {
  project: string;
  item: WorkItemDetail;
  /**
   * Scroll the card into view and focus the first question box (E292): the
   * pane was opened from the Backlog's Ask the author or Check the reply.
   */
  focusRequested?: boolean;
}

/** Keyed by item by the caller, so the dwell timer starts over on every item. */
export function AuthorQuestionsCard({ project, item, focusRequested = false }: AuthorQuestionsCardProps) {
  const itemId = item.ref.id;
  const key = itemKey(project, itemId);
  const queryClient = useQueryClient();
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!focusRequested) return;
    const section = sectionRef.current;
    // Absent in some test DOMs.
    if (section && typeof section.scrollIntoView === "function") section.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [focusRequested]);
  const bridge = useToolboxBridge();
  const { runner } = useRunnerBridge();
  const contract = bridge?.app.contracts.grooming ?? 0;
  // E283: drafting saves a draft (`groom-one`); posting needs `post-single-brief`. The API checks both.
  const mayDraft = useCan(project, "groom-one").allowed;
  const mayPost = useCan(project, "post-single-brief").allowed;
  const draftRunner = mayDraft && runner !== null && contract >= AUTO_DRAFT_MIN_CONTRACT ? runner : null;
  const batchRunning = useBatchRunning(project, draftRunner);
  const draftQuery = useAuthorQuestionsDraft(project, itemId);
  const post = usePostAuthorQuestions({ tracker: item.ref.tracker, container: item.ref.container, id: itemId });
  // E292: the stored ask, keyed like the post (the item's real project), so a post updates it at once.
  const askQuery = useAuthorQuestionsLatestAsk(item.ref.container, itemId);
  const latestAsk = askQuery.data ?? null;
  // The read failed, so whether the author was already asked is unknown: never offer to post
  // then, or a failed read becomes a second comment that emails the author again.
  const askUnknown = askQuery.isError && askQuery.data === undefined;
  const { data: me } = useMe();
  /** Ask again opens the editor over the asked view; a successful post closes it. */
  const [askingAgain, setAskingAgain] = useState(false);
  /** The comment posted but SpecTree could not store the ask: say so, and never offer Post again. */
  const [postedUnrecorded, setPostedUnrecorded] = useState(false);
  // ADO can @mention the creator only with their identity id (the API posts without a mention otherwise).
  const authorName = item.createdBy?.id ? item.createdBy.displayName : null;

  const inFlight = useAuthorDraftStore((state) => state.inFlight);
  const attempted = useAuthorDraftStore((state) => state.attempted[key] === true);
  const result = useAuthorDraftStore((state) => state.results[key]);
  const failure = useAuthorDraftStore((state) => state.failures[key]);

  // E284: a contract-3 app drafts through its run queue.
  const { supported: queueSupported, state: queueState, actions: queueActions } = useGroomingQueue();
  const queueMode = draftRunner !== null && queueSupported;
  const queueStatus = queueMode ? queueBadgeStatus(queueState, safeQueueKey("author-questions", project, itemId), Date.now()) : null;
  const queueActive = queueStatus?.kind === "queued" || queueStatus?.kind === "running" ? queueStatus : null;
  const otherDraftQueued =
    queueMode && queueState !== null && [...queueState.running, ...queueState.upNext].some((entry) => entry.kind === "author-questions");
  const draftKey = workItemKeys.authorQuestionsDraft("ado", project, itemId);
  useOnQueueSettled(queueActive?.entry.id ?? null, () => {
    void queryClient.invalidateQueries({ queryKey: draftKey });
  });

  const stored = draftQuery.data ?? null;
  const current = stored?.isCurrent ? stored : null;
  const drafting = queueMode ? queueActive !== null : inFlight === key;
  const waitingForDraft = draftQuery.isLoading;
  const blockedByBatch = queueMode ? false : batchRunning;
  const anotherInFlight = queueMode ? otherDraftQueued : inFlight !== null;
  const queueFailure =
    queueStatus?.kind === "failed"
      ? (queueStatus.entry.message ?? groomingErrorMessage(queueStatus.entry.errorCode ?? "UNKNOWN").cause)
      : undefined;
  const shownFailure = failure ?? (drafting ? undefined : queueFailure);

  // Auto-draft: only with no current draft, never during a batch (contract 2), once per item, one at a time.
  const shouldDraft =
    draftRunner !== null && draftQuery.isSuccess && current === null && !blockedByBatch && !attempted && !anotherInFlight && !drafting;

  const draftNow = useCallback(() => {
    if (!draftRunner) return;
    if (!queueMode) {
      startDraft(draftRunner, queryClient, project, itemId);
      return;
    }
    useAuthorDraftStore.setState((state) => {
      const { [key]: _old, ...failures } = state.failures;
      return { attempted: { ...state.attempted, [key]: true }, failures };
    });
    // UX-PM-26: the bridge call carries no title (no contract change), so remember it here for the Queue screen.
    const queueKey = safeQueueKey("author-questions", project, itemId);
    if (queueKey) rememberQueueTitle(queueKey, item.title);
    void queueActions.enqueueAuthorQuestions(project, itemId).then((result) => {
      if (!result.ok) {
        useAuthorDraftStore.setState((state) => ({ failures: { ...state.failures, [key]: result.message } }));
      }
    });
  }, [draftRunner, queueMode, queryClient, project, itemId, key, queueActions, item.title]);

  useEffect(() => {
    if (!shouldDraft) return undefined;
    const timer = setTimeout(draftNow, AUTO_DRAFT_DWELL_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [shouldDraft, draftNow]);

  // What to show: the current stored draft, then this run's result, then an out-of-date draft.
  const questions = current?.questions ?? result ?? stored?.questions ?? null;
  const outOfDate = current === null && result === undefined && stored !== null;

  const copyQuestions = (list: readonly string[]) => {
    if (list.length === 0) return;
    const text = list.map((q, index) => `${String(index + 1)}. ${q}`).join("\n");
    // Absent outside a secure context, whatever the DOM types say.
    const clipboard = navigator.clipboard as Clipboard | undefined;
    if (!clipboard) {
      showGroomingToast("The questions could not be copied.", "info");
      return;
    }
    void clipboard.writeText(text).then(
      () => showGroomingToast("Questions copied"),
      () => showGroomingToast("The questions could not be copied.", "info")
    );
  };
  const copy = () => {
    if (questions) copyQuestions(questions);
  };

  const postQuestions = (list: string[]) => {
    // The Queue's finished entry for this item says "posted" from now on (on this computer).
    // The promise, unlike a per-call callback, still resolves if this card unmounts first.
    const queueKey = safeQueueKey("author-questions", project, itemId);
    void post.mutateAsync(list).then(
      (result) => {
        if (queueKey) rememberQuestionsPosted(queueKey);
        setAskingAgain(false);
        if (!result.ask) setPostedUnrecorded(true);
      },
      // The hook's error state shows the failure under the button; the edits stay.
      () => undefined
    );
  };

  const showAsked = latestAsk !== null && !askingAgain && !postedUnrecorded;
  // Ask again starts from the questions as last posted; Claude's draft only before the first ask.
  const askedAgainFrom = askingAgain ? latestAsk : null;
  const editorQuestions = askedAgainFrom?.questions ?? questions;
  const askedOn = askedAgainFrom ? shortDate(askedAgainFrom.createdAt) : null;
  const showEditor =
    mayPost && !askUnknown && !showAsked && !postedUnrecorded && !drafting && editorQuestions !== null && editorQuestions.length > 0;

  return (
    <section
      ref={sectionRef}
      id="author-questions"
      className="glass-card flex scroll-mt-4 flex-col gap-2.5 rounded-[10px] px-4 py-3.5"
      data-testid="author-questions"
    >
      {/* UX-PM-30: the heading on its own line, its status chip and activity line under it, so it never wraps beside them. */}
      <div className="flex flex-col items-start gap-1.5" data-testid="author-questions-header">
        <h2 className="font-display text-[13px] font-semibold text-zinc-200">Questions for the author</h2>
        {showAsked ? (
          <Chip tone="ok" testId="author-questions-asked-chip">
            <Check className="h-3 w-3" aria-hidden="true" />
            Asked
          </Chip>
        ) : showEditor ? (
          <span className="text-xs text-muted-foreground" data-testid="author-questions-editing-caption">
            {askedAgainFrom ? `As asked${askedOn ? ` on ${askedOn}` : ""} · edit before posting again` : "Drafted by Claude · edit before posting"}
          </span>
        ) : null}
        {queueActive && <QueueBadge status={queueActive} />}
        {drafting && (!queueActive || queueActive.kind === "running") && (
          <span className={cn("flex items-center gap-1.5 text-[13px]", TOOLBOX_TEXT.faint)} role="status">
            <span className="h-3 w-3 animate-spin rounded-full border-2 border-link border-r-transparent" aria-hidden="true" />
            Drafting from the item and the code
          </span>
        )}
      </div>

      {postedUnrecorded ? (
        <p className="text-[13px] leading-normal text-muted-foreground" role="status" data-testid="author-questions-posted">
          <a href={item.ref.url} target="_blank" rel="noreferrer" className="font-semibold text-green-400 hover:underline">
            Posted to ADO.
          </a>{" "}
          SpecTree could not record it, so the item does not show as asked here. Do not post the questions again.
        </p>
      ) : showAsked ? (
        <AskedView
          ask={latestAsk}
          itemUrl={item.ref.url}
          onAskAgain={
            mayPost
              ? () => {
                  post.reset();
                  setAskingAgain(true);
                }
              : undefined
          }
        />
      ) : drafting || askQuery.isLoading ? (
        <Shimmer />
      ) : askUnknown && mayPost ? (
        <p className="text-[13px] leading-normal text-muted-foreground" role="alert" data-testid="author-questions-ask-unknown">
          SpecTree could not check whether the author was already asked, so posting is off for now.{" "}
          <button
            type="button"
            className="font-medium text-link hover:underline"
            onClick={() => {
              void askQuery.refetch();
            }}
          >
            Read again
          </button>
        </p>
      ) : showEditor ? (
        <>
          {outOfDate && !askedAgainFrom && (
            <p className="text-[13px] text-amber-400" data-testid="author-questions-out-of-date">
              Drafted before the item last changed. Check they still apply.
            </p>
          )}
          <AuthorQuestionsEditor
            key={JSON.stringify(editorQuestions)}
            initial={editorQuestions}
            authorName={authorName}
            posterName={me?.name ?? null}
            pending={post.isPending}
            onPost={postQuestions}
            onCopy={copyQuestions}
            autoFocus={askingAgain || focusRequested}
          />
          {post.isError && (
            <p className="text-sm text-destructive" role="alert">
              The comment was not posted. Try again in a moment.
            </p>
          )}
          {askingAgain && (
            <button
              type="button"
              className="self-start text-xs text-muted-foreground hover:text-foreground hover:underline"
              onClick={() => {
                setAskingAgain(false);
              }}
            >
              Cancel
            </button>
          )}
        </>
      ) : questions && questions.length > 0 ? (
        <>
          <ul className="flex list-disc flex-col gap-1.5 pl-[18px] text-sm leading-normal" data-testid="author-question-list">
            {questions.map((question, index) => (
              <li key={index}>{question}</li>
            ))}
          </ul>
          {outOfDate && (
            <p className="text-[13px] text-amber-400" data-testid="author-questions-out-of-date">
              Drafted before the item last changed. Check they still apply.
            </p>
          )}
          <div className="mt-1 flex items-center gap-2">
            <button
              type="button"
              className="inline-flex h-[30px] items-center gap-1.5 rounded-lg border border-[#27272A] bg-[#1A1A1E] px-2.5 text-[13px] font-medium hover:bg-[#222227]"
              onClick={copy}
            >
              <Copy className="h-3.5 w-3.5" aria-hidden="true" />
              Copy
            </button>
          </div>
        </>
      ) : waitingForDraft ? (
        <Shimmer />
      ) : draftRunner ? (
        shownFailure ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-destructive" role="alert">
              {shownFailure}
            </p>
            <button
              type="button"
              className="self-start text-sm text-link hover:underline disabled:opacity-50"
              disabled={anotherInFlight || blockedByBatch}
              onClick={draftNow}
            >
              Try again
            </button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="author-questions-waiting">
            {attempted
              ? "Claude drafted no questions for this item."
              : blockedByBatch
              ? "Questions are drafted when the batch run finishes."
              : anotherInFlight
                ? "Drafted after the questions for another item."
                : "Questions are drafted when you stay on the item."}
          </p>
        )
      ) : !mayDraft ? (
        <p className="text-sm text-muted-foreground" data-testid="author-questions-none">
          No questions drafted for this item yet.
        </p>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid="author-questions-in-toolbox">
          Questions are drafted in SpecTree Toolbox.
        </p>
      )}
    </section>
  );
}
